import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Download, Calendar, Eye, User, ChevronLeft, ChevronRight, X, FileText, Lock } from 'lucide-react';
import api from '../api/axiosInstance';
import { PermissionManager } from '../utils/PermissionManager';
import { ErrorBoardPost, ErrorBoardFile } from './types';
import ErrorBoardComments from './ErrorBoardComments';
import './css/error-board.css';

const ErrorBoardDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<ErrorBoardPost | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [currentUserName, setCurrentUserName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [lastLoadedId, setLastLoadedId] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageFiles, setImageFiles] = useState<ErrorBoardFile[]>([]);

  // 날짜 선택 모달 관련 상태
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [dateModalType, setDateModalType] = useState<'start' | 'end'>('start');
  const [selectedDate, setSelectedDate] = useState<string>('');

  // 영문 상태 → 한글 변환
  const convertStatusToKorean = (status: string | undefined): string => {
    if (!status) return '신규 등록';

    const statusMap: { [key: string]: string } = {
      'PENDING': '신규 등록',
      'WAITING': '작업 대기',
      'IN_PROGRESS': '작업 중',
      'COMPLETED': '작업 완료',
      'ON_HOLD': '작업 보류',
      '신규 등록': '신규 등록',
      '작업 대기': '작업 대기',
      '작업 중': '작업 중',
      '작업 완료': '작업 완료',
      '작업 보류': '작업 보류'
    };

    return statusMap[status] || status;
  };

  // 한글 상태 → 영문 변환
  const convertStatusToEnglish = (status: string): string => {
    const statusMap: { [key: string]: string } = {
      '신규 등록': 'PENDING',
      '작업 대기': 'WAITING',
      '작업 중': 'IN_PROGRESS',
      '작업 완료': 'COMPLETED',
      '작업 보류': 'ON_HOLD'
    };

    return statusMap[status] || status;
  };

  useEffect(() => {
    if (lastLoadedId === id) return;

    const initializeDetail = async () => {
      try {
        const userNameRes = await api.get('/api/UserName');
        const userInfo = userNameRes.data;
        setCurrentUserId(userInfo.id ? String(userInfo.id) : '');
        setCurrentUserName(userInfo.name || '');

        setLoading(true);
        const postRes = await api.get<ErrorBoardPost>(`/api/error-board/${id}`);
        setPost(postRes.data);
        setLastLoadedId(id || null);
      } catch (error) {
        console.error('Failed to initialize:', error);
        alert('게시글을 불러오는데 실패했습니다.');
        navigate('/error-board');
      } finally {
        setLoading(false);
      }
    };

    initializeDetail();
  }, [id]);

  useEffect(() => {
    if (post && post.files) {
      // Quill 이미지 제외하고 attachment만 미리보기에 포함
      const images = post.files.filter(file =>
        file.contentType && file.contentType.startsWith('image/') && file.source !== 'quill'
      );
      setImageFiles(images);
    }
  }, [post]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileDownload = async (fileId: string | number, fileName: string) => {
    try {
      const response = await api.get(`/api/error-board/file/${fileId}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('File download failed:', error);
      alert('파일 다운로드에 실패했습니다.');
    }
  };

  // PDF 미리보기 함수
  const handlePdfPreview = async (fileId: string | number) => {
    try {
      const response = await api.get(`/api/error-board/file/${fileId}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('PDF preview failed:', error);
      alert('PDF 미리보기에 실패했습니다.');
    }
  };

  const canEditPost = () => {
    if (!post) return false;
    return PermissionManager.canModifyAllBoardPosts() ||
           (post.authorId && post.authorId === currentUserId);
  };

  const canDeletePost = () => {
    if (!post) return false;
    return PermissionManager.canModifyAllBoardPosts() ||
           (post.authorId && post.authorId === currentUserId);
  };

  const handleEdit = () => {
    navigate(`/error-board/write?id=${post?.id}`);
  };

  const handleDelete = async () => {
    if (!post) return;

    try {
      await api.delete(`/api/error-board/${post.id}`);
      alert('게시글이 삭제되었습니다.');
      navigate('/error-board');
    } catch (error) {
      console.error('Delete failed:', error);
      alert('게시글 삭제에 실패했습니다.');
    }
    setDeleteModalOpen(false);
  };

  // 실제 상태 업데이트 함수 (모든 필드 명시적 지정)
  const updateStatusWithData = async (
    newStatus: string,
    assignee: string | undefined | null,
    assigneeId: number | undefined | null,
    workStartDate: string | undefined | null,
    workEndDate: string | undefined | null
  ) => {
    if (!post) return;

    try {
      const formData = new FormData();
      const postData: any = {
        ...post,
        status: convertStatusToEnglish(newStatus),
        assignee: assignee,
        assigneeId: assigneeId,
        workStartDate: workStartDate,
        workEndDate: workEndDate,
        isStatusUpdateOnly: true  // 진행 상태만 변경 (관리자만 가능, modifiedDate 업데이트 안함)
      };

      formData.append('post', new Blob([JSON.stringify(postData)], { type: 'application/json' }));

      const response = await api.put(`/api/error-board/${post.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setPost(response.data);
      alert('진행 상태가 업데이트되었습니다.');
    } catch (error) {
      console.error('Status update failed:', error);
      alert('진행 상태 업데이트에 실패했습니다.');
    }
  };

  // 상태 변경 핸들러
  const handleStatusChange = async (newStatus: string) => {
    if (!post) return;

    // 작업 중 선택 시: 담당자 + 시작일 설정
    if (newStatus === '작업 중') {
      setDateModalType('start');
      // 현재 날짜와 시간을 datetime-local 형식으로 설정
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      setSelectedDate(`${year}-${month}-${day}T${hours}:${minutes}`);
      setDateModalOpen(true);
    }
    // 작업 완료 선택 시: 완료일 설정 (담당자, 시작일 유지)
    else if (newStatus === '작업 완료') {
      setDateModalType('end');
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      setSelectedDate(`${year}-${month}-${day}T${hours}:${minutes}`);
      setDateModalOpen(true);
    }
    // 신규 등록: 모든 정보 초기화
    else if (newStatus === '신규 등록') {
      await updateStatusWithData(newStatus, null, null, null, null);
    }
    // 작업 대기: 모든 정보 초기화
    else if (newStatus === '작업 대기') {
      await updateStatusWithData(newStatus, null, null, null, null);
    }
    // 작업 보류: 담당자는 유지하되, 시작일과 완료일은 초기화
    else if (newStatus === '작업 보류') {
      await updateStatusWithData(newStatus, post.assignee, post.assigneeId, null, null);
    }
  };

  // 날짜 확인 버튼 클릭
  const handleDateConfirm = async () => {
    if (!post || !selectedDate) return;

    const newStatus = dateModalType === 'start' ? '작업 중' : '작업 완료';

    if (dateModalType === 'start') {
      // 작업 중: 담당자 + 시작일 설정
      await updateStatusWithData(newStatus, currentUserName, Number(currentUserId), selectedDate, null);
    } else {
      // 작업 완료: 시작일이 없으면 완료일과 동일하게 설정, 있으면 유지
      const startDate = post.workStartDate || selectedDate;
      const assigneeName = post.assignee || currentUserName;
      const assigneeIdValue = post.assigneeId || Number(currentUserId);
      await updateStatusWithData(newStatus, assigneeName, assigneeIdValue, startDate, selectedDate);
    }

    setDateModalOpen(false);
  };

  const openImageModal = (index: number) => {
    setCurrentImageIndex(index);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
  };

  const goToPrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? imageFiles.length - 1 : prev - 1
    );
  };

  const goToNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === imageFiles.length - 1 ? 0 : prev + 1
    );
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!imageModalOpen) return;

      if (e.key === 'Escape') {
        closeImageModal();
      } else if (e.key === 'ArrowLeft') {
        goToPrevImage();
      } else if (e.key === 'ArrowRight') {
        goToNextImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [imageModalOpen, imageFiles.length]);

  if (loading) {
    return (
      <div className="board-container">
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="board-container">
        <div className="error-message">게시글을 찾을 수 없습니다.</div>
      </div>
    );
  }

  return (
    <div className="board-container">
      <header className="board-header">
        <h1 className="board-title">오류 제보</h1>
      </header>

      <div className="post-detail">
        <div className="post-header">
          <h1 className="post-title">
            {post.title}
            {post.isPrivate && (
              <span title="비공개 글" className="private-lock-icon">
                <Lock size={20} color="#666" />
              </span>
            )}
          </h1>

          <div className="post-meta">
            <div className="meta-item">
              <User size={16} />
              <span>{post.author}</span>
            </div>
            <div className="meta-item">
              <Calendar size={16} />
              <span>{formatDate(post.createdDate)}</span>
            </div>
            {post.modifiedDate && post.modifiedDate !== post.createdDate && (
              <div className="meta-item modified">
                <span>수정: {formatDate(post.modifiedDate)}</span>
              </div>
            )}
            <div className="meta-item">
              <Eye size={16} />
              <span>{post.viewCount}</span>
            </div>
          </div>

          {/* 진행 상태 관리 영역 */}
          <div className="post-meta post-meta-status">
            {PermissionManager.canModifyAllBoardPosts() ? (
              <div className="meta-item">
                <span className="status-label">진행 상태:</span>
                <select
                  value={convertStatusToKorean(post.status)}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className={`status-badge status-dropdown status-${convertStatusToKorean(post.status).replace(' ', '-')}`}
                >
                  <option value="신규 등록">신규 등록</option>
                  <option value="작업 대기">작업 대기</option>
                  <option value="작업 중">작업 중</option>
                  <option value="작업 완료">작업 완료</option>
                  <option value="작업 보류">작업 보류</option>
                </select>
              </div>
            ) : (
              <div className="meta-item">
                <span className="status-label">진행 상태:</span>
                <span className={`status-badge status-${convertStatusToKorean(post.status).replace(' ', '-')}`}>
                  {convertStatusToKorean(post.status)}
                </span>
              </div>
            )}

            {post.assignee && (
              <div className="meta-item">
                <User size={16} />
                <span>담당자: {post.assignee}</span>
              </div>
            )}

            {post.workStartDate && (
              <div className="meta-item">
                <Calendar size={16} />
                <span>시작: {formatDateTime(post.workStartDate)}</span>
              </div>
            )}

            {post.workEndDate && (
              <div className="meta-item">
                <Calendar size={16} />
                <span>완료: {formatDateTime(post.workEndDate)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="post-content">
          <div
            className="content-body ql-editor"
            dangerouslySetInnerHTML={{
              __html: post.content
            }}
          />
        </div>

        {/* 첨부파일 - attachment 소스만 표시 (quill 이미지 제외) */}
        {post.files && post.files.filter(f => f.source !== 'quill').length > 0 && (
          <div className="post-files">
            <h3 className="files-title">첨부파일</h3>
            <div className="files-list">
              {post.files.filter(f => f.source !== 'quill').map((file, index) => (
                <div key={file.id} className="file-item">
                  <div className="file-info">
                    <span
                      className="file-name file-name-link"
                      onClick={() => handleFileDownload(file.id, file.originalName)}
                    >
                      {file.originalName}
                    </span>
                    <span className="file-size">({formatFileSize(file.size)})</span>
                  </div>
                  <div className="file-actions">
                    {/* 다운로드 버튼 */}
                    <button
                      className="file-action-btn-text"
                      onClick={() => handleFileDownload(file.id, file.originalName)}
                      title="다운로드"
                    >
                      <Download size={14} />
                      다운로드
                    </button>
                    {/* 이미지 파일인 경우 미리보기 버튼 */}
                    {file.contentType.startsWith('image/') && (
                      <button
                        className="file-action-btn-text"
                        onClick={() => {
                          const imgIndex = imageFiles.findIndex(img => img.id === file.id);
                          if (imgIndex !== -1) {
                            openImageModal(imgIndex);
                          }
                        }}
                        title="미리보기"
                      >
                        <Eye size={14} />
                        미리보기
                      </button>
                    )}
                    {/* PDF 파일인 경우 미리보기 버튼 */}
                    {file.contentType === 'application/pdf' && (
                      <button
                        className="file-action-btn-text"
                        onClick={() => handlePdfPreview(file.id)}
                        title="미리보기"
                      >
                        <FileText size={14} />
                        미리보기
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 댓글 섹션 */}
        <ErrorBoardComments
          boardId={Number(id)}
          boardAuthorId={Number(post.authorId)}
          currentUserId={currentUserId}
          currentUserName={currentUserName}
        />

        <div className="post-actions-container">
          <div className="post-actions-left">
            {canEditPost() && (
              <button className="edit-btn" onClick={handleEdit}>
                <Edit size={16} />
                수정
              </button>
            )}
            {canDeletePost() && (
              <button className="delete-btn" onClick={() => setDeleteModalOpen(true)}>
                <Trash2 size={16} />
                삭제
              </button>
            )}
          </div>
          <button className="back-to-list-btn" onClick={() => navigate('/error-board')}>
            <ArrowLeft size={16} />
            목록으로
          </button>
        </div>
      </div>

      {/* 삭제 확인 모달 */}
      {deleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-title-row">
              <h2>게시글 삭제</h2>
              <button
                className="modal-close-btn"
                onClick={() => setDeleteModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-desc">
              이 게시글을 정말 삭제하시겠습니까?<br />
              삭제된 게시글은 복구할 수 없습니다.
            </div>
            <div className="modal-btn-row">
              <button
                className="modal-delete-btn"
                onClick={handleDelete}
              >
                삭제
              </button>
              <button
                className="modal-cancel-btn"
                onClick={() => setDeleteModalOpen(false)}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 날짜 선택 모달 */}
      {dateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-title-row">
              <h2>{dateModalType === 'start' ? '작업 시작일 선택' : '작업 완료일 선택'}</h2>
              <button
                className="modal-close-btn"
                onClick={() => setDateModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-desc">
              <input
                type="datetime-local"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="date-input"
              />
            </div>
            <div className="modal-btn-row">
              <button
                className="modal-confirm-btn"
                onClick={handleDateConfirm}
              >
                확인
              </button>
              <button
                className="modal-cancel-btn"
                onClick={() => setDateModalOpen(false)}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이미지 모달 */}
      {imageModalOpen && imageFiles.length > 0 && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <button
            className="modal-close-btn"
            onClick={(e) => {
              e.stopPropagation();
              closeImageModal();
            }}
          >
            <X size={24} />
          </button>

          {imageFiles.length > 1 && (
            <button
              className="modal-nav-btn modal-nav-prev"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevImage();
              }}
            >
              <ChevronLeft size={32} />
            </button>
          )}

          {imageFiles.length > 1 && (
            <button
              className="modal-nav-btn modal-nav-next"
              onClick={(e) => {
                e.stopPropagation();
                goToNextImage();
              }}
            >
              <ChevronRight size={32} />
            </button>
          )}

          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={imageFiles[currentImageIndex]?.url || `${process.env.REACT_APP_API_HOST}/api/error-board/image/${imageFiles[currentImageIndex]?.id}`}
              alt={imageFiles[currentImageIndex]?.originalName}
              className="modal-image"
            />
          </div>

          <div className="modal-bottom-info">
            {imageFiles.length > 1 && (
              <div className="modal-image-counter">
                {currentImageIndex + 1} / {imageFiles.length}
              </div>
            )}

            <div className="modal-image-filename">
              {imageFiles[currentImageIndex]?.originalName}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorBoardDetail;
